// SCRUM-438: pure formatting for the Event Ops "QR scans" funnel card.
// Kept out of EventOps.jsx, which pulls in axios via Services/arndAPI.js and
// can't be imported into a Jest test here (see funnelStages.js for the same
// reason).
//
// Generic-link scans (bare arnd.io/app hits — printed cards, the WordPress
// redirect) are credited by time: startsAt <= scan time <= endsAt. The API
// (QrScansService.forEvent) adds a `generic: { total, ios, android, other,
// from, to, sharedWith }` object to `funnel.qrScans`, or `generic: null`
// when the WordPress endpoint was unreachable — that must render as nothing
// extra, not an error state.

/** The bare-link source key the API calls 'qr' relabelled as "no tag" —
 * it may not have come from a QR code at all. */
export const sourceLabel = (key) => (key === 'qr' ? 'no tag' : key);

/** Source-split line, e.g. "no tag 12 · sms 4 · email 2", sorted by count
 * descending. '' when there's no bySource breakdown. */
export const bySourceLine = (bySource) => {
  if (!bySource) return '';
  return Object.entries(bySource)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${sourceLabel(k)} ${v}`)
    .join(' · ');
};

/** "Campaign clicks: sms 3 · email 1" line, or '' when there are none. */
export const campaignLine = (campaign) => {
  if (!campaign || Object.keys(campaign).length === 0) return '';
  return `Campaign clicks: ${Object.entries(campaign)
    .map(([k, v]) => `${k} ${v}`)
    .join(' · ')}`;
};

/** "arnd.io/app/<slug>: N" line for an event with its own link, or '' when
 * the event has no slug. */
export const slugLine = (slug, slugTotal) => (slug ? `arnd.io/app/${slug}: ${slugTotal}` : '');

/** "Generic link during the event: M" line, or '' when `generic` is null
 * (WordPress endpoint unreachable — must degrade to nothing extra, not an
 * error). */
export const genericLine = (generic) => (generic ? `Generic link during the event: ${generic.total}` : '');

/** Names of other events the same generic hits are also credited to
 * (overlapping event days), or [] when there are none or generic is null. */
export const sharedWithNames = (generic) => (generic?.sharedWith ?? []).map((e) => e.name);

/** Amber "Also counted for" note text, or '' when nothing overlaps. */
export const sharedWithNote = (generic) => {
  const names = sharedWithNames(generic);
  return names.length ? `Also counted for: ${names.join(', ')} (overlapping event days)` : '';
};

export const QR_SCANS_TOOLTIP = "Scans of the bare arnd.io/app link (e.g. printed cards) between the event's start and end are credited to this event.";

/** The card's one-line summary (it was crowded on a phone): the event link's
 * scans and the generic link's, e.g. "wcc2 40 · generic 12". '' when there is
 * neither (no slug, generic unavailable). */
export const qrSummaryLine = (q) => {
  if (!q) return '';
  const parts = [];
  if (q.slug) parts.push(`${q.slug} ${q.slugTotal}`);
  if (q.generic) parts.push(`generic ${q.generic.total}`);
  return parts.join(' · ');
};

/** Short amber note for the card itself; the event names go in details. */
export const sharedWithShort = (generic) => {
  const n = sharedWithNames(generic).length;
  return n ? `⚠ generic shared with ${n} event${n === 1 ? '' : 's'}` : '';
};
